/**
 * The per-tab 2D/3D landing (brief two, section 1).
 *
 * "Every view is reached two ways: from the view switcher inside 3D
 * mode, and by the tab's own 2D/3D toggle (so a user on the Cascade tab
 * who flips to 3D lands on the Cascade view, not the constellation)."
 *
 * Both halves live here so a tab adds three lines rather than
 * re-deriving the lazy import, the error boundary, the WebGL fallback
 * and the persistence rule -- four things the constellation had to get
 * right once and nobody should have to get right again.
 *
 * The choice is stored PER TAB. It is a real preference: an operator
 * may want the cascade as a flat impact list and the constellation in
 * depth, and one global switch would make each visit overwrite the
 * other's.
 */
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import type * as Twin3DModuleNs from "./Twin3D";

type Twin3DModule = typeof Twin3DModuleNs;
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { haltScenes } from "./sceneHalt";
import { Twin3DBoundary } from "./Twin3DBoundary";
import type { ViewId } from "./views/types";

// Lazy so a 2D view never pays for the 3D bundle (three.js is ~138 kB
// gzipped; measured A/B in the P405 spike).
/** Round 19 E23 -- the scene's code, fetched once. The cascade's first
 *  press in a fresh browser spent 1.1 s of 2.6 to 3.0 downloading this
 *  chunk; a reader who points at or tabs to "3D" starts that download
 *  before the press. */
let twin3dChunk: Promise<Twin3DModule> | null = null;
export function prefetchTwin3D(): Promise<Twin3DModule> {
  twin3dChunk ??= import("./Twin3D");
  return twin3dChunk;
}
const Twin3D = lazy(() => prefetchTwin3D().then((m) => ({ default: m.Twin3D })));

export type ViewMode = "2d" | "3d";

/**
 * Storage keys are explicit rather than derived from the view id: the
 * constellation shipped first and its operators already have a saved
 * preference under this exact key. Deriving a new one would silently
 * discard it.
 */
export const VIEW_MODE_KEYS: Record<ViewId, string> = {
  constellation: "marsad.constellation.viewMode",
  cascade: "marsad.twin.cascade.viewMode",
  journey: "marsad.twin.journey.viewMode",
  fragility: "marsad.twin.fragility.viewMode",
  city: "marsad.twin.capmap.viewMode",
};

export interface Twin3DModeState {
  /** What the operator picked. */
  mode: ViewMode;
  /** What to actually render -- 2D when WebGL let us down. */
  effectiveMode: ViewMode;
  setMode: (m: ViewMode) => void;
  onWebglFail: (reason?: "load") => void;
  /** ROUND 16 K15 -- the scene's code did not load (a deploy landed
   *  mid-session), as distinct from WebGL being unavailable. The page
   *  shows 2D, the toggle reads 2D, and the toggle carries the notice. */
  loadFailed: boolean;
}

export function useTwin3DMode(view: ViewId): Twin3DModeState {
  const key = VIEW_MODE_KEYS[view];
  // `webglFailed` is kept SEPARATE from the choice on purpose. Falling
  // back must not rewrite what the operator picked: WebGL can be
  // unavailable transiently (a lost context, a busy GPU), and letting
  // the fallback persist would silently destroy a 3D preference the
  // next load could have honoured.
  const [webglFailed, setWebglFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  /**
   * Round 10 A1 -- which dimension a reader is in belongs in the URL.
   *
   * Both dimensions lived at one address, so "send a colleague what you
   * are looking at" could not survive the flat/3D choice. The URL WINS
   * over the stored preference when it carries one: a link is somebody
   * saying "look at this", and the recipient's own last visit is not an
   * argument against it.
   */
  const [params, setParams] = useSearchParams();
  const urlMode = params.get("mode");
  const [mode, setModeState] = useState<ViewMode>(() => {
    if (urlMode === "3d" || urlMode === "2d") return urlMode;
    try {
      return window.localStorage.getItem(key) === "3d" ? "3d" : "2d";
    } catch {
      return "2d";
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(key, mode);
    } catch {
      /* private mode: the toggle still works, it just will not persist. */
    }
  }, [key, mode]);

  /* Round 16 E12 -- what is on screen now, read inside the callback
     without making it a dependency. */
  const effectiveRef = useRef<ViewMode>(mode);
  effectiveRef.current = webglFailed ? "2d" : mode;

  const setMode = useCallback(
    (m: ViewMode) => {
      /* Round 16 E12 -- the toggle offers both buttons, so "3D" can be
         pressed on a scene already in 3D. The halt below then stopped
         the loop and nothing remounted, so nothing resumed it: the
         scene froze for good, and a drag drew nothing at all. A press
         that changes nothing does nothing. */
      if (m === effectiveRef.current) return;
      /* ROUND 13 C2 -- tear the scene down BEFORE repainting rather
         than competing with it. At 20,000 items this toggle did not
         answer at all, timing out with the button visible, enabled and
         stable: the click was queued behind a frame that cost a second.
         Halting first gives the main thread back to the navigation that
         asked to leave. */
      haltScenes();
      setWebglFailed(false);
      setLoadFailed(false);
      setModeState(m);
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("mode", m);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return {
    mode,
    effectiveMode: webglFailed ? "2d" : mode,
    setMode,
    onWebglFail: useCallback((reason?: "load") => {
      setWebglFailed(true);
      if (reason === "load") setLoadFailed(true);
    }, []),
    loadFailed,
  };
}

/** The tab's own 2D | 3D segmented control. */
export function Twin3DToggle({
  mode,
  onChange,
  testid = "twin-view",
  loadFailed = false,
}: {
  /** The mode the page is SHOWING (`effectiveMode`): a toggle left
   *  pressed on 3D over a 2D view tells the reader nothing failed. */
  mode: ViewMode;
  onChange: (m: ViewMode) => void;
  /** ROUND 16 K15 -- the scene's code did not load; say so here, where
   *  the notice survives the fall back to 2D. */
  loadFailed?: boolean;
  /** Prefix for the BUTTON testids (`{testid}-2d` / `{testid}-3d`); the
   *  group itself takes `{testid}-mode`. Named this way because the
   *  constellation shipped with exactly that pairing and its tests and
   *  the SPA drive both point at those ids. */
  testid?: string;
}) {
  const { t } = useTranslation();
  const group = (
    <div
      data-testid={`${testid}-mode`}
      role="group"
      aria-label={t("modules.twin.constellation3d.modeLabel")}
      className="inline-flex rounded border border-slate-200 bg-white"
    >
      {(["2d", "3d"] as const).map((v) => (
        <button
          key={v}
          type="button"
          data-testid={`${testid}-${v}`}
          data-active={mode === v ? "true" : "false"}
          aria-pressed={mode === v}
          onClick={() => onChange(v)}
          onPointerEnter={v === "3d" ? () => void prefetchTwin3D() : undefined}
          onFocus={v === "3d" ? () => void prefetchTwin3D() : undefined}
          className={
            "px-3 py-1 text-xs " +
            (mode === v
              ? "bg-brand-brass text-brand-navy font-medium"
              : "text-slate-600 hover:bg-slate-50")
          }
        >
          {t(`modules.twin.constellation3d.mode${v === "2d" ? "2d" : "3d"}`)}
        </button>
      ))}
    </div>
  );
  if (!loadFailed) return group;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {group}
      <SceneCouldNotLoad compact />
    </span>
  );
}

/**
 * Round 12 E3 -- what a reader sees while the scene's code arrives.
 *
 * This was a bare dark rectangle with nothing in it, and driving the
 * scenes repeatedly found the state it produces: on two of eight loads
 * the page carried no canvas, was not falling back to 2D and was not
 * reporting WebGL unsupported -- it was waiting for the chunk, and
 * saying nothing. A black box with no words is indistinguishable from a
 * scene that has failed, which is the administrator's stated fear about
 * this surface in his own words: a twin that renders empty with nothing
 * saying why.
 *
 * It keeps the stage's own colour and reserves its height, so what
 * arrives lands where the box was rather than shifting the page under
 * somebody who has started to look.
 */
function SceneLoading() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="twin3d-scene-loading"
      className="flex-1 rounded-lg p-6"
      style={{ background: "#070C18", minHeight: 640 }}
    >
      <p className="text-sm" style={{ color: "#b9b3a4" }}>
        {t("modules.twin.constellation3d.sceneLoading")}
      </p>
    </div>
  );
}

/**
 * The 3D stage for one tab: boundary, suspense and the shell, landing
 * on this tab's own view.
 */
export function Twin3DStage({
  view,
  onWebglFail,
  pinnedIds,
  focusId,
}: {
  view: ViewId;
  onWebglFail: () => void;
  /** ROUND 15 E5 -- the page's search reaches the scene. */
  pinnedIds?: ReadonlySet<string>;
  focusId?: string | null;
}) {
  return (
    <Twin3DBoundary onError={onWebglFail} fallback={<SceneCouldNotLoad />}>
      <Suspense fallback={<SceneLoading />}>
        <Twin3D
          initialView={view}
          onFallback={onWebglFail}
          pinnedIds={pinnedIds}
          focusId={focusId}
        />
      </Suspense>
    </Twin3DBoundary>
  );
}

/**
 * ROUND 15 K8 -- the boundary's fallback was `null`, so a chunk the
 * new build no longer served (a deploy landing mid-session) put the 3D
 * view back to 2D with nothing on screen and no word about why. Say it,
 * and offer the one thing that fixes it.
 */
export function SceneCouldNotLoad({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="twin3d-load-failed"
      role="status"
      className={
        compact
          ? "inline-flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1"
          : "flex-1 rounded-lg border border-amber-200 bg-amber-50 p-4"
      }
    >
      <p className={compact ? "text-xs text-amber-900" : "text-sm text-amber-900"}>
        {t("modules.twin.constellation3d.couldNotLoad")}
      </p>
      <button
        type="button"
        data-testid="twin3d-reload"
        onClick={() => window.location.reload()}
        className={
          (compact ? "" : "mt-2 ") +
          "rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark hover:bg-brand-brass/10"
        }
      >
        {t("modules.twin.constellation3d.reload")}
      </button>
    </div>
  );
}
